import { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { getGeneralRules, updateGeneralRule } from "../APIs/api-routes.util";
import { Alert } from "../components/ui/Alert";
import { LoadingOverlay } from "../components/ui/PagePrimitives";
import { useAppSettings } from "../context/AppSettingsContext";
import { apiSubscribe } from "../lib/api-subscribe";
import { cn } from "../lib/cn";

export interface WeeklyRule {
  id: number;
  weekday: string;
  day_status: string;
  urgent_limit: number;
  normal_limit: number;
  appointment_close: number;
  is_open: boolean;
  start_time: string | null;
  end_time: string | null;
}

type LimitField = "urgent_limit" | "normal_limit";

const DAY_SHORT: Record<string, string> = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

function Switch({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-40",
        checked ? "bg-brand-600" : "bg-slate-300"
      )}
    >
      <span
        className={cn(
          "inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200",
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        )}
      />
    </button>
  );
}

function LimitStepper({
  label,
  value,
  tone,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  tone: "rose" | "emerald";
  disabled?: boolean;
  onChange: (next: number) => void;
}) {
  const toneBorder = tone === "rose" ? "border-accent-rose/40" : "border-accent-emerald/40";
  const toneText = tone === "rose" ? "text-accent-rose-dark" : "text-accent-emerald-dark";
  const toneBtn = tone === "rose" ? "hover:bg-accent-rose-light" : "hover:bg-accent-emerald-light";

  return (
    <div className="min-w-0">
      <p className={cn("mb-1 text-center text-2xs font-semibold uppercase", toneText)}>{label}</p>
      <div
        className={cn(
          "flex h-8 items-center overflow-hidden rounded-lg border bg-surface",
          toneBorder
        )}
      >
        <button
          type="button"
          disabled={disabled || value <= 0}
          onClick={() => onChange(value - 1)}
          className={cn("flex h-full w-7 items-center justify-center disabled:opacity-30", toneBtn)}
          aria-label={`Decrease ${label}`}
        >
          <FontAwesomeIcon icon={faMinus} className="text-2xs" />
        </button>
        <span className="flex h-full min-w-[2rem] flex-1 items-center justify-center border-x border-inherit text-sm font-bold tabular-nums">
          {value}
        </span>
        <button
          type="button"
          disabled={disabled || value >= 99}
          onClick={() => onChange(value + 1)}
          className={cn("flex h-full w-7 items-center justify-center disabled:opacity-30", toneBtn)}
          aria-label={`Increase ${label}`}
        >
          <FontAwesomeIcon icon={faPlus} className="text-2xs" />
        </button>
      </div>
    </div>
  );
}

function WeeklySchedule() {
  const { urgentEnabled } = useAppSettings();
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [rules, setRules] = useState<WeeklyRule[]>([]);
  const [error, setError] = useState("");
  const [flashId, setFlashId] = useState<number | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout>>();
  const loadCleanup = useRef<(() => void) | null>(null);

  const load = useCallback(() => {
    loadCleanup.current?.();
    setLoading(true);
    setError("");
    loadCleanup.current = apiSubscribe(
      getGeneralRules(),
      {
        next: (body) => {
          setLoading(false);
          const data = body as { status: string; rules: WeeklyRule[] };
          if (data?.status !== "success" || !Array.isArray(data.rules)) {
            setError("Could not load weekly schedule.");
            return;
          }
          setRules(data.rules);
        },
        error: () => {
          setLoading(false);
          setError("Could not reach server.");
        },
      }
    );
  }, []);

  useEffect(() => {
    load();
    return () => {
      loadCleanup.current?.();
      if (flashTimer.current) clearTimeout(flashTimer.current);
    };
  }, [load]);

  const flashSaved = (id: number) => {
    setFlashId(id);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashId(null), 1200);
  };

  const applyRuleUpdate = (updated: WeeklyRule) => {
    setRules((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    flashSaved(updated.id);
  };

  const handleToggle = (rule: WeeklyRule) => {
    setSavingId(rule.id);
    setError("");
    updateGeneralRule(rule.id, { enabled: !rule.is_open }).subscribe({
      next: (res: any) => {
        setSavingId(null);
        const updated = res?.response?.rule;
        if (updated) applyRuleUpdate(updated);
        else setError(res?.response?.message || "Update failed.");
      },
      error: () => {
        setSavingId(null);
        setError("Could not update day status.");
      },
    });
  };

  const persistLimit = (ruleId: number, field: LimitField, value: number) => {
    setSavingId(ruleId);
    setError("");
    updateGeneralRule(ruleId, { [field]: value }).subscribe({
      next: (res: any) => {
        setSavingId(null);
        const updated = res?.response?.rule;
        if (updated) applyRuleUpdate(updated);
        else setError(res?.response?.message || "Could not save limit.");
      },
      error: () => {
        setSavingId(null);
        setError("Could not save limit.");
      },
    });
  };

  const openCount = rules.filter((r) => r.is_open).length;
  const sampleOpen = rules.find((r) => r.is_open);

  return (
    <>
      <LoadingOverlay show={loading} />

      {error && (
        <Alert variant="error" onDismiss={() => setError("")}>
          {error}
        </Alert>
      )}

      <div className="overflow-hidden rounded-2xl bg-header-bar text-white shadow-soft">
        <div className="px-4 py-3">
          <p className="text-sm font-semibold">Weekly schedule</p>
          <p className="mt-0.5 text-2xs text-white/75">
            {urgentEnabled
              ? "Set urgent & normal booking counts for each open weekday"
              : "Set normal booking counts for each open weekday"}
          </p>
        </div>
        <div className="grid grid-cols-3 divide-x divide-white/15 border-t border-white/15 bg-black/10">
          <div className="px-3 py-2.5 text-center">
            <p className="text-lg font-bold tabular-nums leading-none">{openCount}</p>
            <p className="mt-1 text-2xs text-white/70">Open</p>
          </div>
          <div className="px-3 py-2.5 text-center">
            <p className="text-lg font-bold tabular-nums leading-none">{7 - openCount}</p>
            <p className="mt-1 text-2xs text-white/70">Closed</p>
          </div>
          <div className="px-3 py-2.5 text-center">
            <p className="text-lg font-bold tabular-nums leading-none">
              {sampleOpen
                ? urgentEnabled
                  ? sampleOpen.urgent_limit + sampleOpen.normal_limit
                  : sampleOpen.normal_limit
                : "—"}
            </p>
            <p className="mt-1 text-2xs text-white/70">Example total</p>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-brand-100 bg-surface shadow-soft">
        <div
          className={cn(
            "grid items-end gap-2 border-b border-brand-100 bg-brand-50/80 px-3 py-2 text-2xs font-semibold uppercase tracking-wide text-brand-700",
            urgentEnabled
              ? "grid-cols-[2.25rem_1fr_4.25rem_4.25rem]"
              : "grid-cols-[2.25rem_1fr_4.25rem]"
          )}
        >
          <span>Day</span>
          <span>Status</span>
          {urgentEnabled && <span className="text-center">Urgent</span>}
          <span className="text-center">Normal</span>
        </div>

        <ul className="divide-y divide-brand-50">
          {rules.map((rule) => {
            const busy = savingId === rule.id;
            const saved = flashId === rule.id;

            return (
              <li
                key={rule.id}
                className={cn(
                  "grid items-center gap-2 px-3 py-2.5 transition-colors duration-200",
                  urgentEnabled
                    ? "grid-cols-[2.25rem_1fr_4.25rem_4.25rem]"
                    : "grid-cols-[2.25rem_1fr_4.25rem]",
                  !rule.is_open && "bg-slate-50/90",
                  busy && "opacity-60",
                  saved && "bg-brand-50/70"
                )}
              >
                <span
                  className={cn(
                    "text-sm font-bold",
                    rule.is_open ? "text-brand-800" : "text-content-muted"
                  )}
                >
                  {DAY_SHORT[rule.weekday] ?? rule.weekday.slice(0, 3)}
                </span>

                <div className="flex min-w-0 items-center gap-2">
                  <Switch
                    checked={rule.is_open}
                    disabled={busy}
                    onChange={() => handleToggle(rule)}
                    label={`${rule.weekday} bookings`}
                  />
                  <span
                    className={cn(
                      "truncate text-2xs font-medium",
                      rule.is_open ? "text-accent-emerald-dark" : "text-content-muted"
                    )}
                  >
                    {rule.is_open ? "Open" : "Closed"}
                  </span>
                </div>

                {rule.is_open ? (
                  <>
                    {urgentEnabled && (
                      <LimitStepper
                        label="Urgent"
                        value={rule.urgent_limit}
                        tone="rose"
                        disabled={busy}
                        onChange={(next) => {
                          if (next === rule.urgent_limit) return;
                          persistLimit(rule.id, "urgent_limit", next);
                        }}
                      />
                    )}
                    <LimitStepper
                      label="Normal"
                      value={rule.normal_limit}
                      tone="emerald"
                      disabled={busy}
                      onChange={(next) => {
                        if (next === rule.normal_limit) return;
                        persistLimit(rule.id, "normal_limit", next);
                      }}
                    />
                  </>
                ) : (
                  <>
                    {urgentEnabled && (
                      <span className="text-center text-2xs text-content-muted">—</span>
                    )}
                    <span className="text-center text-2xs text-content-muted">—</span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}

export default WeeklySchedule;
